<!--
  EndOfPour — the 14 Yes / No / N-A items of spec Appendix B.

  The paper's closing checklist. Three decisions this file makes:

  * One tap answers an item and flushes it. There is no separate save step,
    because fourteen items with a save button each is slower than the book.

  * Items answered "No" are collected into a standing summary. A "No" here is
    an outstanding action ("all hold/stop events documented and closed",
    "records provided to Kiewit/QC for review"), and the whole reason the paper
    failed was that unfilled lines went unnoticed. It is a prompt, NOT a block:
    the spec is explicit that the only hard block in the app is closing a pour
    with an unresolved failed Hold point.

  * Unanswered items are counted in the header, so a half-finished checklist is
    obvious at a glance (spec, Screens 4).
-->
<script lang="ts">
  import type {
    EndOfPourItem,
    EndOfPourStatus,
    UpdateEndOfPourItemRequest
  } from '../../lib/types';
  import InitialsTimeField from '../../lib/fields/InitialsTimeField.svelte';
  import NotesField from '../../lib/fields/NotesField.svelte';
  import YesNoField from '../../lib/fields/YesNoField.svelte';
  import { clearDraft, loadDraft, saveDraft } from '../../lib/fields/local-draft';

  interface Props {
    pourId: number;
    /** The 14 Appendix B items; rendered by item_no. */
    items: EndOfPourItem[];
    shiftName?: string | null;
    readonly?: boolean;
    onUpdate?: (itemId: number, body: UpdateEndOfPourItemRequest) => Promise<void>;
  }

  let { pourId, items, shiftName = null, readonly = false, onUpdate }: Props = $props();

  const STATUS_OPTIONS = [
    { value: 'yes' as EndOfPourStatus, label: 'Yes', tone: 'good' as const },
    { value: 'no' as EndOfPourStatus, label: 'No', tone: 'bad' as const },
    { value: 'na' as EndOfPourStatus, label: 'N-A', tone: 'neutral' as const }
  ];

  /* ------------------------------------------------------------------ draft */

  type Draft = Partial<Pick<EndOfPourItem, 'status' | 'notes' | 'initials'>>;

  const DRAFT_KEY = `end-of-pour.${pourId}`;

  interface DraftState {
    values: Record<number, Draft>;
    dirty: Record<number, string[]>;
  }

  const restored = loadDraft<DraftState>(DRAFT_KEY);
  let values = $state<Record<number, Draft>>(restored?.values ?? {});
  let dirty = $state<Record<number, string[]>>(restored?.dirty ?? {});

  let saving = $state<Record<number, boolean>>({});
  let errors = $state<Record<number, string | null>>({});
  let expanded = $state<Record<number, boolean>>({});

  $effect(() => {
    const snapshot: DraftState = { values: $state.snapshot(values), dirty: $state.snapshot(dirty) };
    if (Object.keys(snapshot.values).length === 0) clearDraft(DRAFT_KEY);
    else saveDraft(DRAFT_KEY, snapshot);
  });

  let rows = $derived(
    [...items]
      .sort((a, b) => a.item_no - b.item_no)
      .map((base) => ({ base, view: { ...base, ...(values[base.id] ?? {}) } as EndOfPourItem }))
  );

  let answered = $derived(rows.filter((r) => r.view.status !== 'pending').length);
  let outstanding = $derived(rows.filter((r) => r.view.status === 'pending'));
  let negatives = $derived(rows.filter((r) => r.view.status === 'no'));

  function isDirty(id: number): boolean {
    return (dirty[id]?.length ?? 0) > 0;
  }

  function set<K extends keyof Draft>(id: number, key: K, v: Draft[K]) {
    if (readonly) return;
    if (!values[id]) values[id] = {};
    values[id][key] = v;
    const keys = dirty[id] ?? [];
    if (!keys.includes(key as string)) dirty[id] = [...keys, key as string];
  }

  async function flush(id: number) {
    const pending = dirty[id];
    if (readonly || !pending || pending.length === 0 || saving[id]) return;

    const draft = values[id] ?? {};
    const patch: Record<string, unknown> = {};
    for (const key of pending) patch[key] = (draft as Record<string, unknown>)[key];

    saving[id] = true;
    errors[id] = null;
    try {
      await onUpdate?.(id, patch as UpdateEndOfPourItemRequest);
      const still = (dirty[id] ?? []).filter(
        (k) => (values[id] as Record<string, unknown>)?.[k] !== patch[k]
      );
      if (still.length === 0) delete dirty[id];
      else dirty[id] = still;
      prune(id);
    } catch (e) {
      // The draft is never discarded because a request failed.
      errors[id] = e instanceof Error ? e.message : 'Could not save. Tap retry.';
    } finally {
      saving[id] = false;
    }
  }

  function prune(id: number) {
    const base = items.find((t) => t.id === id);
    const draft = values[id];
    if (!base || !draft) return;
    for (const k of Object.keys(draft) as (keyof Draft)[]) {
      if (dirty[id]?.includes(k as string)) continue;
      if ((base as unknown as Record<string, unknown>)[k as string] === draft[k]) delete draft[k];
    }
    if (Object.keys(draft).length === 0) delete values[id];
  }

  function setStatus(id: number, status: EndOfPourStatus | null) {
    set(id, 'status', status ?? 'pending');
    flush(id);
  }
</script>

<section class="eop" aria-label="End of pour checklist">
  <header class="head">
    <h2>End of pour</h2>
    <span class="progress" class:complete={outstanding.length === 0}>
      {answered} of {rows.length} answered
    </span>
  </header>

  {#if negatives.length > 0}
    <div class="summary no" role="status">
      <p class="summary-title">
        {negatives.length} answered “No”
      </p>
      <ul>
        {#each negatives as r (r.base.id)}
          <li>
            <button type="button" onclick={() => (expanded[r.base.id] = true)}>
              {r.view.item_no}. {r.view.title}
            </button>
          </li>
        {/each}
      </ul>
      <p class="summary-note">
        Recorded, not blocked. Add a note saying what is outstanding and who was told.
      </p>
    </div>
  {/if}

  {#if outstanding.length > 0}
    <p class="summary pending" role="status">
      {outstanding.length} item{outstanding.length === 1 ? '' : 's'} still unanswered.
    </p>
  {/if}

  <ol class="rows">
    {#each rows as { base, view } (base.id)}
      {@const open = expanded[base.id] ?? false}
      <li
        class="item"
        class:answered={view.status !== 'pending'}
        class:negative={view.status === 'no'}
        class:unsaved={isDirty(base.id)}
      >
        <div class="itemhead">
          <span class="no">{view.item_no}</span>
          <button
            type="button"
            class="title"
            aria-expanded={open}
            onclick={() => (expanded[base.id] = !open)}
          >
            {view.title}
          </button>
          <span class="state" aria-live="polite">
            {#if saving[base.id]}
              <span class="pill saving">Saving…</span>
            {:else if isDirty(base.id)}
              <span class="pill dirty">Unsaved</span>
              <button type="button" class="retry" onclick={() => flush(base.id)}>Save</button>
            {/if}
          </span>
        </div>

        <YesNoField
          label="Answer for item {view.item_no}"
          hideLabel
          compact
          value={view.status === 'pending' ? null : view.status}
          options={STATUS_OPTIONS}
          disabled={readonly}
          onValue={(v) => setStatus(base.id, v)}
        />

        {#if errors[base.id]}
          <p class="banner bad" role="alert">
            {errors[base.id]}
            <button type="button" class="retry" onclick={() => flush(base.id)}>Retry</button>
          </p>
        {/if}

        {#if open || view.notes}
          <div class="body">
            <NotesField
              value={view.notes}
              collapsible={!open}
              disabled={readonly}
              onValue={(v) => set(base.id, 'notes', v)}
              onCommit={() => flush(base.id)}
            />
            {#if open}
              <InitialsTimeField
                label="SCI initials / time"
                initials={view.initials}
                time={base.recorded_at}
                {shiftName}
                timeEditable={false}
                disabled={readonly}
                compact
                onInitials={(v) => set(base.id, 'initials', v)}
                onCommit={() => flush(base.id)}
              />
            {/if}
          </div>
        {/if}
      </li>
    {/each}
  </ol>
</section>

<style>
  .eop {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .head {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  h2 {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 800;
  }

  .progress {
    margin-left: auto;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--fg-muted, #3f4750);
  }

  .progress.complete {
    color: var(--good, #1a7f45);
  }

  .summary {
    margin: 0;
    padding: 0.75rem;
    border-radius: 0.625rem;
    font-size: 0.9375rem;
    font-weight: 600;
  }

  .summary.pending {
    border: 2px dashed var(--border, #9aa3ad);
    color: var(--fg-muted, #3f4750);
  }

  .summary.no {
    border: 3px solid var(--warn, #a35a00);
    background: var(--warn-bg, #fff6e6);
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .summary-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 900;
    color: var(--warn, #8a4b00);
  }

  .summary-note {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--fg-muted, #3f4750);
  }

  .summary ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .summary ul button {
    width: 100%;
    text-align: left;
    min-height: 2.75rem;
    padding: 0.375rem 0.5rem;
    font: inherit;
    font-size: 0.9375rem;
    font-weight: 700;
    border: 2px solid var(--warn, #a35a00);
    border-radius: 0.5rem;
    background: var(--surface, #fff);
    color: var(--warn, #8a4b00);
    cursor: pointer;
    touch-action: manipulation;
  }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
    border: 2px solid var(--border, #9aa3ad);
    border-left-width: 4px;
    border-radius: 0.625rem;
    background: var(--surface, #fff);
  }

  .item.answered {
    border-left-color: var(--good, #1a7f45);
  }

  .item.negative {
    border-left-color: var(--warn, #a35a00);
    background: var(--warn-bg, #fff6e6);
  }

  .item.unsaved {
    border-color: var(--warn, #a35a00);
  }

  .itemhead {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .no {
    font-weight: 900;
    font-variant-numeric: tabular-nums;
    min-width: 1.5rem;
  }

  .title {
    flex: 1 1 10rem;
    min-width: 0;
    text-align: left;
    min-height: 2.75rem;
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.25;
    color: var(--fg, #10151b);
    cursor: pointer;
    touch-action: manipulation;
  }

  .title:focus-visible {
    outline: 3px solid var(--accent, #0b63c5);
    outline-offset: 2px;
  }

  .state {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-left: auto;
  }

  .pill {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
  }

  .pill.dirty {
    background: var(--warn-bg, #fff6e6);
    color: var(--warn, #8a4b00);
  }

  .pill.saving {
    background: var(--surface-muted, #eef1f4);
    color: var(--fg-muted, #3f4750);
  }

  .banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.5rem 0.625rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .banner.bad {
    background: var(--bad-bg, #fdecea);
    color: var(--bad, #b3261e);
  }

  .retry {
    min-height: 2.5rem;
    padding: 0 0.75rem;
    font: inherit;
    font-weight: 800;
    border: 2px solid currentColor;
    border-radius: 0.4rem;
    background: transparent;
    color: inherit;
    cursor: pointer;
    touch-action: manipulation;
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
</style>
